import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { axiosInstances } from "./apiConfig";

function EnquiryPage() {
  const [formData, setFormData] = useState({
    enquiryMode: "",
    customer: {
      name: "",
      email: "",
      phone: "",
    },
    products: [{ description: "" }],
    subject: "",
    message: "",
    enq_status: "",
    assignedTo: "",
    remarks: "",
  });

  const [mode, setMode] = useState("new"); // 'new' or 'search'
  const [searchId, setSearchId] = useState("");
  const [searchMessage, setSearchMessage] = useState("");
  const [isEditable, setIsEditable] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success' | 'error' | 'info' | 'warning'
  });


  // --------------------
  // Helpers
  // --------------------

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      customer: { ...formData.customer, [name]: value },
    });
  };

  const handleProductChange = (index, value) => {
    const updatedProducts = [...formData.products];
    updatedProducts[index].description = value;
    setFormData({ ...formData, products: updatedProducts });
  };

  const addProduct = () => {
    setFormData({
      ...formData,
      products: [...formData.products, { description: "" }],
    });
  };

  const removeProduct = (index) => {
    const updatedProducts = formData.products.filter((_, i) => i !== index);
    setFormData({ ...formData, products: updatedProducts });
  };

  const handleModeChange = (e) => {
    const newMode = e.target.value;
    setMode(newMode);
    setSearchId("");
    setSearchMessage("");
    setFormData({
      enquiryMode: "",
      customer: { name: "", email: "", phone: "" },
      products: [{ description: "" }],
      subject: "",
      message: "",
      enq_status: "",
      assignedTo: "",
      remarks: "",
    });
    setIsEditable(newMode === "new");
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosInstances.enquiryApi.post("/save", formData);
      console.log("Create Enquiry Response:", response.data);

      const createdId = response.data?.data?.id || response.data?.id || null;

      setSnackbar({
        open: true,
        message: createdId
          ? `✅ Enquiry created successfully with ID: ${createdId}`
          : "✅ Enquiry created successfully.",
        severity: "success",
      });

      setFormData({
        enquiryMode: "",
        customer: { name: "", email: "", phone: "" },
        products: [{ description: "" }],
        subject: "",
        message: "",
        enq_status: "",
        assignedTo: "",
        remarks: "",
      });
    } catch (error) {
      console.error("Submit error:", error);
      setSnackbar({
        open: true,
        message: "❌ Failed to submit enquiry.",
        severity: "error",
      });
    }
  };

  const handleSearch = async () => {
    if (!searchId) {
      setSearchMessage("❌ Please enter an ID to search.");
      return;
    }

    try {
      const response = await axiosInstances.enquiryApi.get(`/${searchId}`);
      const result = response.data;

      if (!result || !result.data) {
        setFormData({
          id: null, // clear ID
          enquiryMode: "",
          customer: { name: "", email: "", phone: "" },
          products: [{ description: "" }],
          subject: "",
          message: "",
          enq_status: "",
          assignedTo: "",
          remarks: "",
        });
        setSearchMessage(`❌ Enquiry with ID ${searchId} does not exist.`);
        setIsEditable(false);
        setIsEditing(false);
        return;
      }

      const { enquiry, customer, products } = result.data;

      setFormData({
        id: enquiry.id, // ✅ include ID here
        enquiryMode: enquiry.enquiryMode || "",
        customer: {
          id: customer?.id,
          name: customer?.name || "",
          email: customer?.email || "",
          phone: customer?.phone || "",
        },
        products:
          products?.length > 0
            ? products.map((p) => ({ description: p.description || "" }))
            : [{ description: "" }],
        subject: enquiry.subject || "",
        message: enquiry.message || "",
        enq_status: enquiry.enq_status || "", // your backend expects enq_status
        assignedTo: enquiry.assignedTo || "",
        remarks: enquiry.remarks || "",
      });

      setIsEditable(false);
      setIsEditing(false);
      setSearchMessage("✅ Enquiry loaded successfully.");
    } catch (error) {
      console.error("Search failed:", error);

      setFormData({
        id: null, // clear ID
        enquiryMode: "",
        customer: { name: "", email: "", phone: "" },
        products: [{ description: "" }],
        subject: "",
        message: "",
        enq_status: "",
        assignedTo: "",
        remarks: "",
      });
      setSearchMessage(`❌ Enquiry with ID ${searchId} does not exist.`);
      setIsEditable(false);
      setIsEditing(false);
    }
  };




  const handleEditOrSave = async () => {
    if (isEditing) {
      try {
        await axiosInstances.enquiryApi.post("/update", formData);
        setIsEditing(false);
        setIsEditable(false);
        setSearchMessage("✅ Enquiry updated successfully.");
      } catch (error) {
        console.error("Update error:", error);
        setSearchMessage("❌ Failed to update enquiry.");
      }
    } else {
      setIsEditing(true);
      setIsEditable(true);
    }
  };

  const handleDelete = async () => {
    if (!searchId) {
      setSearchMessage("❌ Please enter an ID to delete.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this enquiry?"))
      return;

    try {
      await axiosInstances.enquiryApi.post("/delete", {id: searchId,});
      setFormData({
        enquiryMode: "",
        customer: { name: "", email: "", phone: "" },
        products: [{ description: "" }],
        subject: "",
        message: "",
        enq_status: "",
        assignedTo: "",
        remarks: "",
      });
      setSearchId("");
      setSearchMessage(`✅ Enquiry ${searchId} deleted successfully.`);
      setIsEditable(false);
      setIsEditing(false);
    } catch (error) {
      console.error("Delete error:", error);
      setSearchMessage("❌ Failed to delete enquiry.");
    }
  };

  // --------------------
  // JSX
  // --------------------

  return (
    <div style={{ maxWidth: "700px", margin: "40px auto" }}>
      <h2>Enquiry Management</h2>

      {/* Mode toggle */}
      <div>
        <label>
          <input
            type="radio"
            value="new"
            checked={mode === "new"}
            onChange={handleModeChange}
          />
          New
        </label>
        &nbsp;&nbsp;
        <label>
          <input
            type="radio"
            value="search"
            checked={mode === "search"}
            onChange={handleModeChange}
          />
          Search
        </label>
      </div>

      {/* Search */}
      {mode === "search" && (
        <div style={{ margin: "12px 0" }}>
          <input
            type="text"
            placeholder="Search by Enquiry ID"
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            style={{ width: "50%", padding: "8px", fontSize: "14px" }}
          />
          <button onClick={handleSearch}>🔍 Search</button>
          {searchMessage && (
            <div
              style={{
                marginTop: "6px",
                color: searchMessage.startsWith("✅") ? "green" : "red",
              }}
            >
              {searchMessage}
            </div>
          )}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <label>*Customer Name:</label>
        <br />
        <input
          type="text"
          name="name"
          value={formData.customer.name}
          onChange={handleCustomerChange}
          disabled={!isEditable}
          required
        />
        <br />
        <br />

        <label>Email:</label>
        <br />
        <input
          type="email"
          name="email"
          value={formData.customer.email}
          onChange={handleCustomerChange}
          disabled={!isEditable}
        />
        <br />
        <br />

        <label>*Phone:</label>
        <br />
        <input
          type="tel"
          name="phone"
          value={formData.customer.phone}
          onChange={handleCustomerChange}
          disabled={!isEditable}
          required
        />
        <br />
        <br />

        <label>Enquiry Mode:</label>
        <br />
        <select
          name="enquiryMode"
          value={formData.enquiryMode}
          onChange={handleChange}
          disabled={!isEditable}
        >
          <option value="">--select--</option>
          <option value="CALL">CALL</option>
          <option value="EMAIL">EMAIL</option>
          <option value="WHATSAPP">WHATSAPP</option>
        </select>
        <br />
        <br />

        <TextField
          margin="dense"
          label="Enquiry Date"
          name="enquiryDate"
          type="date"
          value={formData.enquiryDate || new Date().toISOString().split("T")[0]}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          disabled={!isEditable}
        />
        <br />
        <br />

        <label>Subject:</label>
        <br />
        <textarea
          name="subject"
          value={formData.subject}
          wrap="off"
          onChange={(e) => {
            handleChange(e);
            const el = e.target;
            el.style.height = "auto";
            el.style.width = "auto";
            el.style.height = el.scrollHeight + "px";
            el.style.width = Math.min(el.scrollWidth + 10, 600) + "px";
          }}
          disabled={!isEditable}
          required
          style={{
            minHeight: "30px",
            minWidth: "200px",
            maxWidth: "100%",
            resize: "none",
            overflow: "hidden",
            transition: "0.1s",
          }}
        />

        <br />
        <br />
        <label>Products:</label>
        <br />
        {formData.products.map((product, index) => (
          <div key={index} style={{ marginBottom: "8px" }}>
            <textarea
              name={`product-${index}`}
              value={product.description}
              wrap="off" // ⬅️ allows width to expand
              onChange={(e) => {
                handleProductChange(index, e.target.value);

                // Auto resize both height & width
                const el = e.target;
                el.style.height = "auto";
                el.style.width = "auto";
                el.style.height = el.scrollHeight + "px";
                el.style.width = Math.min(el.scrollWidth + 10, 600) + "px"; // limit width
              }}
              disabled={!isEditable}
              required
              style={{
                minHeight: "30px",
                minWidth: "200px",
                maxWidth: "100%",
                fontFamily: "monospace",
                resize: "none",
                overflow: "hidden",
                whiteSpace: "pre",
                transition: "0.1s",
              }}
            />

            {/* Remove button */}
            {isEditable && formData.products.length > 1 && (
              <button
                type="button"
                onClick={() => removeProduct(index)}
                style={{
                  marginLeft: "8px",
                  backgroundColor: "red",
                  color: "white",
                  border: "none",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                X
              </button>
            )}
          </div>
        ))}

        {/* Add Product button */}
        {isEditable && (
          <button
            type="button"
            onClick={addProduct}
            style={{
              backgroundColor: "#4CAF50",
              color: "white",
              border: "none",
              padding: "4px 10px",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            + Add Product
          </button>
        )}

        <br />
        <br />

        <label>Message:</label>
        <br />
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          disabled={!isEditable}
          style={{ width: "80%", height: "100px" }}
        />
        <br />
        <br />

        <label>Status:</label>
        <br />
        <select
          name="enq_status"
          value={formData.enq_status}
          onChange={handleChange}
          disabled={!isEditable}
        >
          <option value="">--select--</option>
          <option value="OPEN">OPEN</option>
          <option value="OFFERED">OFFERED</option>
          <option value="CLOSED">CLOSED</option>
        </select>
        <br />
        <br />

        <label>Assigned To:</label>
        <br />
        <input
          type="text"
          name="assignedTo"
          value={formData.assignedTo}
          onChange={handleChange}
          disabled={!isEditable}
        />
        <br />
        <br />

        <label>Remarks:</label>
        <br />
        <textarea
          name="remarks"
          value={formData.remarks}
          onChange={handleChange}
          disabled={!isEditable}
          style={{ width: "80%", height: "80px" }}
        />
        <br />
        <br />

        <label>* fields are mandatory</label>
        <br />
        <br />

        {/* Action Buttons */}
        {mode === "new" && <button type="submit">Submit Enquiry</button>}

        {mode === "search" && (
          <div style={{ display: "flex", gap: "10px" }}>
            <button type="button" onClick={handleEditOrSave}>
              {isEditing ? "Save" : "Edit"}
            </button>
            <button
              type="button"
              onClick={handleDelete}
              style={{ backgroundColor: "red", color: "white" }}
            >
              Delete
            </button>
          </div>
        )}
      </form>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </div>
  );
}

export default EnquiryPage;
